// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Ben Richardson <hi@ben.gy>
//
// Every piece of jargon this site uses, defined for someone who has never read
// a farm P&L. Assume the reader knows nothing: "broadacre", "equity ratio" and
// "imputed labour" are all opaque outside agriculture, and two of them are
// load-bearing for the site's headline.

export interface Term {
  term: string;
  short: string;
  detail?: string;
}

export const GLOSSARY: Record<string, Term> = {
  broadacre: {
    term: 'Broadacre',
    short: 'Large-scale farming of crops and grazing livestock — wheat, barley, canola, wool, sheep and beef cattle — as opposed to intensive industries like horticulture or poultry.',
    detail: 'This site covers broadacre farming and dairy, because those are the industries ABARES surveys. Horticulture, sugar, vegetables, poultry and pigs are NOT surveyed and appear nowhere here. That is why nothing on this site says "Australian farming" without qualification.',
  },
  aagis: {
    term: 'AAGIS',
    short: 'The Australian Agricultural and Grazing Industries Survey — the annual ABARES survey of broadacre farms that produces almost every number on this site.',
    detail: 'ABARES visits a stratified sample of farms each year and collects a full financial and physical account of each business. The results are published as estimates for 32 regions. It is a sample, not a census: no figure here is a count of all farms.',
  },
  rateOfReturn: {
    term: 'Rate of return',
    short: 'What a farm earned in a year, as a percentage of the capital tied up in it — the farming equivalent of a return on investment.',
    detail: 'ABARES publishes two versions, and the difference between them is the whole point of this site. EXCLUDING capital appreciation counts only what the farm business earned by farming. INCLUDING capital appreciation adds the change in the value of the land and other assets. Both are used exactly as ABARES publishes them and are never recalculated here.',
  },
  capitalAppreciation: {
    term: 'Capital appreciation',
    short: 'The change over the year in what a farm\'s assets are worth — overwhelmingly the land.',
    detail: 'It is a paper gain: it is real wealth, and it is real collateral for a loan, but it is not cash and it is only realised if the farm is sold. It can also be negative — nationally it was, in eight of the 36 years covered here.',
  },
  profitAtFullEquity: {
    term: 'Profit at full equity',
    short: 'What the farm business earned before the cost of borrowing — i.e. the return to all the capital in the business, however it was financed.',
    detail: 'Using it rather than farm business profit means a heavily mortgaged farm and a debt-free one are compared on what the FARM earned, not on who owns the mortgage.',
  },
  farmCashIncome: {
    term: 'Farm cash income',
    short: 'Total cash receipts minus total cash costs — the money that actually moved through the farm bank account.',
    detail: 'It ignores depreciation, unpaid family labour and changes in the value of stock on hand, so it is always a flattering number compared with farm business profit.',
  },
  farmBusinessProfit: {
    term: 'Farm business profit',
    short: 'Farm cash income after depreciation, after charging for the family\'s own unpaid labour, and after allowing for changes in trading stock.',
    detail: 'This is the honest measure of whether a year of farming made money. It is frequently negative in regions where farm cash income looks positive.',
  },
  imputedLabour: {
    term: 'Imputed labour',
    short: 'A wage charged for the work the farmer and their family do without being paid for it.',
    detail: 'ABARES prices the operator\'s and family\'s hours at an award rate and deducts it as a cost. Without it, a farm that "profits" only because nobody drew a wage would look profitable.',
  },
  equityRatio: {
    term: 'Equity ratio',
    short: 'The share of the farm\'s assets the farmer actually owns, after debt — 100% means no debt.',
  },
  rse: {
    term: 'Relative standard error',
    short: 'How much sampling noise sits on an estimate, as a percentage of the estimate itself. Small is good.',
    detail: 'Because these figures come from a sample of farms rather than all of them, each one is uncertain. An RSE of 10% is solid; ABARES advises treating anything above 25% with caution and anything above 50% as unreliable. This site follows that advice literally — over half its regional cells are above 25%, and it says so on the chart rather than in a footnote.',
  },
  sampleContributing: {
    term: 'Sample',
    short: 'The number of real surveyed farms behind an estimate.',
    detail: 'It ranges from 5 to 170 depending on the region and year. Anything under 15 is treated as unreliable here no matter what its stated error says, because an error estimated from five farms is itself estimated from five farms.',
  },
  zone: {
    term: 'Agro-climatic zone',
    short: 'ABARES\' own three-way split of agricultural Australia: Pastoral, Wheat-Sheep and High Rainfall.',
    detail: 'Pastoral is the arid and semi-arid rangeland where extensive grazing is the only option. Wheat-Sheep is the cropping belt. High Rainfall is the wetter coastal and tableland country. The zone comes from the ABARES boundary file itself, not from anything invented here — and it turns out to explain most of the difference in what farming earns.',
  },
  provisional: {
    term: 'Provisional',
    short: 'The most recent year is an early estimate that ABARES will revise.',
    detail: 'It is drawn in a lighter, hatched style everywhere it appears and is excluded from every trend claim on this site.',
  },
  nominal: {
    term: 'Nominal dollars',
    short: 'Dollars of the day, with no adjustment for inflation.',
    detail: 'Every dollar figure on this site is nominal, because that is how ABARES publishes them. A 1990 dollar bought roughly twice what a 2024 dollar does, so long-run dollar growth on this site overstates real growth. The site\'s headline is a RATIO of two series measured in the same year\'s dollars, so inflation cancels out of it — but any single dollar figure should be read with the year in mind.',
  },
  perFarm: {
    term: 'Per farm',
    short: 'Every dollar and hectare figure is an average PER FARM, not a total for the region.',
    detail: 'ABARES publishes per-farm averages. A region with fewer, larger farms will show bigger per-farm numbers without necessarily producing more in total.',
  },
  financialYear: {
    term: 'Financial year',
    short: 'Years run 1 July to 30 June. "2024" means 2023–24.',
  },
  landShare: {
    term: 'Land share of return',
    short: 'The proportion of the total return to farm capital that came from land appreciating rather than from farming.',
    detail: 'Computed here two ways that agree: as a share of 36 years of summed dollars (75.1%), and from the gap between the two published rate-of-return series (70.4%). A finding that moved depending on which way you averaged it would not be a finding.',
  },
};

export const GLOSSARY_ORDER: string[] = [
  'broadacre', 'aagis', 'zone', 'rateOfReturn', 'capitalAppreciation', 'landShare',
  'profitAtFullEquity', 'farmCashIncome', 'farmBusinessProfit', 'imputedLabour',
  'equityRatio', 'rse', 'sampleContributing', 'provisional', 'nominal', 'perFarm', 'financialYear',
];
